"use client";

import React, { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: "01",
  title: "Discovery",
  badge: "Initiation",
  description: "We begin by understanding your personal and professional situation, long-term goals, and geographic preferences to establish a clear strategic baseline."
}, {
  id: "02",
  title: "Strategy",
  badge: "Planning",
  description: "Our team designs a bespoke international roadmap that aligns residency choices, business organization, and long-term planning into a coherent direction"
}, {
  id: "03",
  title: "Organization",
  badge: "Formation",
  description: "We outline practical structural options and provide guidance on how to organize your activities across jurisdictions in a compliant and sustainable manner."
}, {
  id: "04",
  title: "Implementation",
  badge: "Execution",
  description: "We support you in navigating next steps by coordinating with independent licensed professionals where required, ensuring clarity and consistency in implementation."
}, {
  id: "05",
  title: "Ongoing Review",
  badge: "Refinement",
  description: "We provide periodic strategic review to ensure your international positioning remains aligned with your evolving objectives."
}];
export default function OurApproach() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [lineHeight, setLineHeight] = useState(0);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementHeight = rect.height;
      const startOffset = windowHeight * 0.75;
      const scrolledDistance = startOffset - rect.top;
      let progress = scrolledDistance / elementHeight;
      progress = Math.min(Math.max(progress, 0), 1);
      setLineHeight(progress * 100);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="our-approach" className="bg-background relative py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-20 transition-all duration-700 transform", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-4xl md:text-5xl font-serif font-medium text-primary mb-6">
            Our Approach
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">A structured journey from complexity to clarity. We guide you through each stage of defining and organizing your international positioning.</p>
        </div>

        <div ref={containerRef} className="relative max-w-4xl mx-auto">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-border -translate-x-1/2 md:transform-none" />
          
          <div className="absolute left-4 md:left-1/2 top-0 w-px bg-ring transition-all duration-100 ease-linear -translate-x-1/2 md:transform-none" style={{
          height: `${lineHeight}%`
        }} />

          <div className="space-y-16 md:space-y-24">
            {steps.map((step, index) => <StepItem key={step.id} step={step} index={index} isLast={index === steps.length - 1} />)}
          </div>
        </div>
      </div>
    </section>;
}
function StepItem({
  step,
  index,
  isLast
}: {
  step: typeof steps[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={cn("relative flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-0 transition-all duration-1000", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
      <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-ring bg-background z-10 flex items-center justify-center mt-1.5 md:mt-0">
        <div className={cn("w-1.5 h-1.5 rounded-full bg-ring transition-transform duration-500", isVisible ? "scale-100" : "scale-0")} />
      </div>

      <div className={cn("pl-12 md:pl-0 md:w-1/2 flex", isEven ? "md:pr-16 md:justify-end md:text-right" : "md:ml-auto md:pl-16 md:justify-start md:text-left")}>
        <div className="relative group max-w-md">
            <span className={cn("absolute -top-8 text-[80px] leading-none font-serif font-bold text-primary/5 select-none pointer-events-none transition-opacity duration-700", isEven ? "right-0 md:-right-6" : "left-0 md:-left-6")} aria-hidden="true">
                {step.id}
            </span>

            <div className="relative z-10">
                <div className={cn("flex items-center gap-3 mb-3", isEven ? "md:flex-row-reverse" : "flex-row")}>
                    <Badge variant="outline" className="border-ring/30 text-ring bg-ring/5 hover:bg-ring/10 px-3 py-1 text-xs uppercase tracking-wider font-medium">
                        {step.badge}
                    </Badge>
                    <div className="h-px w-8 bg-border hidden md:block" />
                </div>
                
                <h3 className="text-2xl md:text-3xl font-serif text-primary mb-3 font-medium">
                    {step.title}
                </h3>
                
                <p className="text-muted-foreground leading-relaxed">
                    {step.description}
                </p>
            </div>
        </div>
      </div>
    </div>;
}